import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 12: IMPACT - Bold Typography, Full-width Images (Inspired by Shopify's Impact theme)
const Template12 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-white min-h-screen font-sans">
            {/* Bold Header */}
            <header className="bg-black text-white py-4">
                <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 text-sm hover:text-gray-300 transition-colors"
                    >
                        <ArrowLeft size={18} /> Back
                    </button>

                    <h1 className="text-3xl font-black tracking-tighter">IMPACT</h1>

                    <button className="px-6 py-2 bg-white text-black text-sm font-bold hover:bg-gray-100 transition-colors">
                        SHOP
                    </button>
                </div>
            </header>

            {/* Full-Width Hero with Bold Text */}
            <section className="relative h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500">
                <div className="absolute inset-0 flex items-center justify-center px-6">
                    <div className="text-center text-white max-w-5xl">
                        <h2 className="text-7xl md:text-9xl font-black leading-none mb-8 tracking-tighter">
                            MAKE AN<br/>IMPACT
                        </h2>
                        <p className="text-2xl md:text-3xl font-bold mb-12">
                            Bold Designs. Bolder Statements.
                        </p>
                        <button className="group px-12 py-6 bg-white text-black text-lg font-black hover:bg-black hover:text-white transition-all">
                            <span>DISCOVER NOW</span>
                            <ArrowRight className="inline-block ml-3 group-hover:translate-x-2 transition-transform" size={24} />
                        </button>
                    </div>
                </div>
            </section>

            {/* Collection Tiles - Full Width */}
            <section className="grid grid-cols-1 md:grid-cols-2">
                {[
                    { title: 'WOMEN', color: 'bg-blue-600', image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200&h=800&fit=crop' },
                    { title: 'MEN', color: 'bg-green-600', image: 'https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?w=1200&h=800&fit=crop' }
                ].map((item, index) => (
                    <div key={index} className="relative h-[600px] overflow-hidden group cursor-pointer">
                        <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className={`absolute inset-0 ${item.color} mix-blend-multiply opacity-60 group-hover:opacity-40 transition-opacity`}></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center text-white">
                                <h3 className="text-6xl md:text-8xl font-black mb-6 tracking-tighter">{item.title}</h3>
                                <button className="px-8 py-4 border-4 border-white text-2xl font-black hover:bg-white hover:text-black transition-all">
                                    SHOP {item.title}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* Featured Products - Grid with Color Blocks */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <div className="text-center mb-16">
                    <h2 className="text-6xl md:text-7xl font-black mb-4 tracking-tighter">NEW DROPS</h2>
                    <p className="text-2xl font-bold">Fresh arrivals weekly</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { bg: 'bg-red-500' },
                        { bg: 'bg-yellow-400' },
                        { bg: 'bg-blue-500' },
                        { bg: 'bg-green-500' },
                        { bg: 'bg-purple-500' },
                        { bg: 'bg-pink-500' },
                        { bg: 'bg-indigo-500' },
                        { bg: 'bg-teal-500' }
                    ].map((item, index) => (
                        <div key={index} className="group cursor-pointer">
                            <div className={`relative aspect-square ${item.bg} mb-4 overflow-hidden`}>
                                <img
                                    src={`https://images.unsplash.com/photo-${1523381210376 + index * 50000}-7cbb3fc41fea?w=600&h=600&fit=crop`}
                                    alt={`Product ${index + 1}`}
                                    className="w-full h-full object-cover mix-blend-overlay opacity-80 transition-all duration-500 group-hover:scale-110 group-hover:opacity-100"
                                />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button className="px-6 py-3 bg-black text-white font-bold text-sm">
                                        QUICK VIEW
                                    </button>
                                </div>
                            </div>
                            <h3 className="text-lg font-black mb-1">ITEM {index + 1}</h3>
                            <p className="text-xl font-bold">$XX.00</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Statement Section */}
            <section className="bg-black text-white py-32">
                <div className="max-w-5xl mx-auto px-6 text-center">
                    <h2 className="text-6xl md:text-8xl font-black mb-8 leading-none tracking-tighter">
                        DESIGNED TO<br/>STAND OUT
                    </h2>
                    <p className="text-2xl md:text-3xl font-bold text-gray-300 mb-12">
                        Not for everyone. Just for you.
                    </p>
                    <button className="px-12 py-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xl font-black hover:from-purple-700 hover:to-pink-700 transition-all">
                        EXPLORE COLLECTION
                    </button>
                </div>
            </section>

            {/* Categories with Overlay Text */}
            <section className="grid grid-cols-1 md:grid-cols-3">
                {[
                    { title: 'TOPS', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=800&h=1000&fit=crop', color: 'from-red-600/80' },
                    { title: 'BOTTOMS', image: 'https://images.unsplash.com/photo-1475178626620-a4d074967452?w=800&h=1000&fit=crop', color: 'from-blue-600/80' },
                    { title: 'SHOES', image: 'https://images.unsplash.com/photo-1460353581641-37baddab0fa2?w=800&h=1000&fit=crop', color: 'from-green-600/80' }
                ].map((cat, index) => (
                    <div key={index} className="relative h-96 overflow-hidden group cursor-pointer">
                        <img
                            src={cat.image}
                            alt={cat.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-t ${cat.color} to-transparent`}></div>
                        <div className="absolute bottom-12 left-12">
                            <h3 className="text-5xl font-black text-white mb-4 tracking-tighter">{cat.title}</h3>
                            <button className="text-white text-lg font-bold underline underline-offset-8">
                                SHOP NOW →
                            </button>
                        </div>
                    </div>
                ))}
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
                        <div>
                            <h3 className="text-4xl font-black mb-6 tracking-tighter">IMPACT</h3>
                            <p className="text-lg font-medium text-gray-400">
                                Making waves in fashion, one bold piece at a time.
                            </p>
                        </div>
                        <div>
                            <h4 className="text-xl font-black mb-4">SHOP</h4>
                            <ul className="space-y-3 text-gray-400 font-medium">
                                <li><a href="#" className="hover:text-white transition-colors">New In</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Best Sellers</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Sale</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xl font-black mb-4">CONNECT</h4>
                            <ul className="space-y-3 text-gray-400 font-medium">
                                <li><a href="#" className="hover:text-white transition-colors">Instagram</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">TikTok</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Twitter</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 pt-8 text-center">
                        <p className="text-gray-500 font-bold">&copy; 2025 IMPACT. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template12;
